import { Disclosure } from '@headlessui/react'
import clsx from 'clsx'
import { Button } from 'components/buttons'
import { Icon } from 'components/icon'
import { Modal, ModalConfirm, NewComponentModal } from 'components/modal'
import {
	ComponentDocuments,
	handleValid,
	SlideCustomText,
	SlideEmail,
	SlideLink,
	SlideLocation,
	SlidePhones,
	SlideTags,
	SlideTitle
} from 'components/myOrganization'
import { ComponentAssets } from 'components/myOrganization/componentAssets'
import { FormResourceOrganization } from 'components/subMenuTabItem'
import { Typography } from 'components/typography'
import {
	Icons,
	TabComponentsMyOrganizationItem,
	TabMyOrganizationItem,
	TypographyTypes
} from 'interfaces'
import { getNameComponentForType } from 'lib'
import React, { FC, useState } from 'react'
import { Controller, Path, useFieldArray } from 'react-hook-form'
import { initialStateModal, optionsComponents } from './optionsNewComponent'

interface ElementComponentProps extends FormResourceOrganization {
	active: number
	nameCard: string
	name: Path<{ tabs: TabMyOrganizationItem[] }>
	resourceUpdated?: boolean
}

export const ElementComponent: FC<ElementComponentProps> = (
	props
): JSX.Element => {
	const {
		active,
		name,
		errors,
		isValid,
		control,
		fieldError,
		nameCard,
		resourceUpdated,
		register,
		setValue,
		watch,
		setErrorForm,
		setFieldError,
		saveData
	} = props

	const { fields, append, remove } = useFieldArray({
		control,
		name: name as `tabs.${number}.setup_menu.${number}.components`
	})
	const [open, setOpen] = useState(false)
	const [modalProps, setModalProps] = useState(initialStateModal)

	const confirmDeleteImage = (deleteComponent: number): void => {
		setModalProps({
			open: true,
			title: 'Changes have been made',
			description: 'Are you sure you want to delete this component?',
			onConfirm: async () => {
				const nameType = `${name}-${deleteComponent}`
				const updatedErrors = fieldError
					.filter(error => !error.includes(nameType))
					.map(error => {
						return error.replace(/components-(\d+)/, (_, num) => {
							const newIndex =
								parseInt(num, 10) > deleteComponent
									? parseInt(num, 10) - 1
									: num
							return `components-${newIndex}`
						})
					})

				setFieldError(updatedErrors)
				handleValid(nameType, true, setFieldError)
				remove(deleteComponent)
				setModalProps(initialStateModal)
				await saveData()
			}
		})
	}

	return (
		<div className="my-4">
			<div className="w-full py-3  mb-3 flex justify-between items-center">
				<Typography
					title={nameCard}
					fontLeading="12/12"
					type={TypographyTypes.p}
					className="text-gray-8"
				/>
			</div>
			<div className="w-full grid grid-cols-1  max-h-[calc(100vh-360px)]  overflow-y-auto mr-2 ">
				{active > -1 &&
					fields.map((component, index) => {
						return (
							<Controller
								control={control}
								name={
									`${name}.${index}` as Path<{
										tabs: TabMyOrganizationItem[]
									}>
								}
								key={`components-${component.id}`}
								render={({ field }) => {
									const type = (field.value as TabComponentsMyOrganizationItem)
										.component_type
									const errorAccordion =
										fieldError &&
										fieldError.length > 0 &&
										fieldError.includes(`${type}-${name}-${index}`)
									if (
										(type === 'assets' || type === 'documents') &&
										!resourceUpdated
									)
										return <div />
									return (
										<div className="mr-2">
											<Disclosure
												key={`component-tab-${
													(field.value as TabComponentsMyOrganizationItem).id
												}`}
											>
												{({ open: openDisclosure }) => (
													<>
														<Disclosure.Button className="w-full flex justify-between items-center rounded-[8px] shadow-md cursor-pointer mt-4 mb-1">
															<div className="w-full flex justify-between items-center p-2 ">
																<div className="flex items-center ">
																	<Typography
																		title={`${getNameComponentForType(
																			(
																				field.value as TabComponentsMyOrganizationItem
																			).component_type
																		)}`}
																		className="text-gray-9"
																		fontLeading="15/18"
																		type={TypographyTypes.span}
																	/>
																	{errorAccordion && !openDisclosure && (
																		<div className="mr-1 w-4 h-4">
																			<Icon
																				src={Icons.error}
																				className="w-7  ml-3 text-red-500"
																				fillPath
																			/>
																		</div>
																	)}
																</div>
																<div className="flex items-center">
																	{openDisclosure && (
																		<div
																			aria-hidden="true"
																			className="z-10 cursor-pointer"
																			onClick={() => {
																				confirmDeleteImage(index)
																			}}
																		>
																			<Icon
																				src={Icons.delete}
																				className="w-7  ml-3 text-gray-1 "
																				fillPath
																			/>
																		</div>
																	)}
																	<Icon
																		fillPath
																		src={Icons.expandMore}
																		className={clsx(
																			openDisclosure
																				? 'rotate-180 transform'
																				: '',
																			'w-7 text-blue-primary ml-3'
																		)}
																	/>
																</div>
															</div>
														</Disclosure.Button>
														<Disclosure.Panel className="text-gray-500 mb-4 p-4 border-x-[0.5px] border-b-[0.5px] border-gray-7 rounded-b-[16px]">
															<div>
																{type === 'title' && (
																	<SlideTitle
																		active={active}
																		pos={index}
																		setValue={setValue}
																		register={register}
																		watch={watch}
																		errors={errors}
																		isValid={isValid}
																		setFieldError={setFieldError}
																		name={name}
																	/>
																)}
																{type === 'simple_text' && (
																	<SlideCustomText
																		active={active}
																		pos={index}
																		setValue={setValue}
																		register={register}
																		watch={watch}
																		errors={errors}
																		isValid={isValid}
																		setFieldError={setFieldError}
																		name={name}
																		control={control}
																	/>
																)}
																{type === 'location' && (
																	<SlideLocation
																		active={active}
																		pos={index}
																		setValue={setValue}
																		register={register}
																		watch={watch}
																		errors={errors}
																		isValid={isValid}
																		setFieldError={setFieldError}
																		name={name}
																	/>
																)}
																{type === 'tags' && (
																	<SlideTags
																		active={active}
																		watch={watch}
																		pos={index}
																		setErrorForm={setErrorForm}
																		setValue={setValue}
																		register={register}
																		errors={errors}
																		setFieldError={setFieldError}
																		name={name}
																	/>
																)}

																{type === 'link' && (
																	<SlideLink
																		active={active}
																		watch={watch}
																		pos={index}
																		setErrorForm={setErrorForm}
																		setValue={setValue}
																		register={register}
																		errors={errors}
																		setFieldError={setFieldError}
																		name={name}
																	/>
																)}

																{type === 'phone' && (
																	<SlidePhones
																		active={active}
																		watch={watch}
																		pos={index}
																		setErrorForm={setErrorForm}
																		setValue={setValue}
																		register={register}
																		errors={errors}
																		setFieldError={setFieldError}
																		name={name}
																	/>
																)}

																{type === 'mail' && (
																	<SlideEmail
																		active={active}
																		watch={watch}
																		pos={index}
																		setErrorForm={setErrorForm}
																		setValue={setValue}
																		register={register}
																		errors={errors}
																		setFieldError={setFieldError}
																		name={name}
																	/>
																)}
																{type === 'assets' && (
																	<ComponentAssets
																		active={active}
																		watch={watch}
																		pos={index}
																		setErrorForm={setErrorForm}
																		setValue={setValue}
																		register={register}
																		errors={errors}
																		setFieldError={setFieldError}
																		name={name}
																	/>
																)}
																{type === 'documents' && (
																	<ComponentDocuments
																		active={active}
																		watch={watch}
																		pos={index}
																		setErrorForm={setErrorForm}
																		setValue={setValue}
																		register={register}
																		errors={errors}
																		setFieldError={setFieldError}
																		name={name}
																	/>
																)}
															</div>
														</Disclosure.Panel>
													</>
												)}
											</Disclosure>
										</div>
									)
								}}
							/>
						)
					})}
			</div>
			{!fields.length && (
				<div className="min-w-[100px] flex justify-center items-center py-3">
					<Typography
						title="Create new content by adding a new component"
						className="text-gray-3"
						fontLeading="15/22"
						type={TypographyTypes.p}
					/>
				</div>
			)}
			<div className="md:mt-1 mt-2 w-full flex justify-end">
				<Button
					label="New Component"
					onClick={() => setOpen(true)}
					className="w-full md:w-auto my-2"
					color="white"
				/>
			</div>

			<Modal
				setOpen={setOpen}
				open={open}
				type="normal"
				title="Select a component the content"
				marginL
				sizeModal="p-4"
				closeButton
			>
				<NewComponentModal
					resourceUpdated={resourceUpdated}
					setOpenModal={setOpen}
					createNewComponent={typeComponent => {
						const nameType = `${typeComponent}-${name}-${fields.length}`
						handleValid(nameType, false, setFieldError)
						const maxOrder = Math.max(...fields.map(c => c.component_order), 0)
						append({
							...optionsComponents[typeComponent],
							component_order: maxOrder + 1
						})
					}}
				/>
			</Modal>

			<ModalConfirm
				open={modalProps.open}
				setOpen={() => setModalProps(initialStateModal)}
				hasCancel
				buttonLabelCancel="Cancel"
				buttonLabel="Yes"
				flexButton="row"
				handleCancel={() => setModalProps(initialStateModal)}
				onClickConfirm={modalProps.onConfirm}
				icon={Icons.warningActive}
				title={modalProps.title}
				descripcion={modalProps.description}
				classNameButton="h-[32px] w-[151px] m-0 !py-0"
			/>
		</div>
	)
}

ElementComponent.defaultProps = {
	resourceUpdated: false
}
